import React from "react";
import { Link } from "react-router-dom";
import {
  FcPackage,
  FcInTransit,
  FcSalesPerformance,
  FcSettings,
  FcStatistics,
  FcOpenedFolder,
  FcInfo
} from "react-icons/fc";

// Reusable Card Component
function Card({ icon, label, link, description }) {
  return (
    <Link to={link} className="block group">
      <div className="bg-white rounded-lg border overflow-hidden transition-all duration-200 hover:shadow h-full">
        <div className="p-4 flex flex-col justify-between h-full">
          <div className="text-center">
            {/* Icon Container */}
            <div className="bg-gray-50 p-3 rounded-lg inline-flex items-center justify-center mb-3 group-hover:scale-105 transition-transform duration-200">
              {React.cloneElement(icon, {
                className: "text-xl",
              })}
            </div>

            {/* Content */}
            <div>
              <h3 className="text-sm font-bold mb-1 text-gray-800">{label}</h3>
              {description && (
                <p className="text-xs text-gray-600 leading-tight">
                  {description}
                </p>
              )}
            </div>
          </div>

          {/* Action Indicator */}
          <div className="flex justify-center mt-3">
            <div className="bg-gray-100 px-3 py-1 rounded text-xs font-medium transition-all duration-200 group-hover:bg-gray-200">
              Get Started →
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function Inventory() {
  const cards = [
    {
      icon: <FcPackage />,
      label: "Inventory Items",
      description: "Manage your products and stock levels",
      link: "/inventory/inventory-items",
    },
    {
      icon: <FcSalesPerformance />,
      label: "Purchases",
      description: "Track purchases and orders",
      link: "/inventory/inventory-purchases",
    },
    {
      icon: <FcSettings />,
      label: "Inventory System",
      description: "Complete inventory management",
      link: "/inventory/inventory-system",
    },
    {
      icon: <FcInTransit />,
      label: "Suppliers",
      description: "Add and manage your suppliers",
      link: "/inventory/inventory-suppliers",
    },
    {
      icon: <FcStatistics />,
      label: "Analytics",
      description: "View inventory reports and insights",
      link: "/inventory/analytics",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-sm border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <FcSettings className="text-2xl" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-800">Inventory Dashboard</h1>
              <p className="text-sm text-gray-600">Central hub for managing your inventory operations</p>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs text-gray-600">Total Items</div>
              <div className="text-lg font-bold text-gray-800">1,247</div>
              <div className="text-xs text-gray-500">In stock</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs text-gray-600">Low Stock</div>
              <div className="text-lg font-bold text-yellow-600">23</div>
              <div className="text-xs text-gray-500">Need reorder</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs text-gray-600">Suppliers</div>
              <div className="text-lg font-bold text-gray-800">15</div>
              <div className="text-xs text-gray-500">Active partners</div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <FcSettings className="mr-2" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.slice(0, 4).map((card, index) => (
              <Card key={index} {...card} />
            ))}
          </div>
        </div>

        {/* Additional Features */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <FcStatistics className="mr-2" />
            Advanced Features
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cards.slice(4).map((card, index) => (
              <Card key={index} {...card} />
            ))}
          </div>
        </div>

        {/* Bottom Info Section */}
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-3 mb-3 md:mb-0">
              <div className="bg-blue-50 p-2 rounded">
                <FcInfo className="text-lg" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Need Help with Inventory?</h3>
                <p className="text-gray-600 text-xs">Check our documentation or contact support</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm">
                View Docs
              </button>
              <button className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors text-sm">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Inventory;